import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router';
import { IContactSelection, ICreateCampaignRequest, ILocationState } from '../../../../../models';
import { getDisplayName } from '../../../../../models/NameUtils';
import { debounce } from '../../../../../models/UiUtils';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { toast } from 'sonner';
import { useModal } from '../../../../../models/hooks/useModal';
import {
	invalidateAllContactFilterQueries,
	useBulkContactToggleTextSubscriptionMutation,
	useContactDeleteManyMutation,
	useContactDeleteMutation,
	useContactSetKeepInTouchMutation,
	useContactSetVisibilityMutation,
	useContactUnarchiveMutation,
	useExportContactsMutation,
	useExportContactsPolicyReportMutation,
} from '../../../../../queries';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../components/ConfirmationDialog';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { ExportConfirmationModalV2 } from '../../../../components/ExportConfirmation';
import { HandwrittenCardTemplateSearchModal } from '../../../../components/HandwrittenCards/HandwrittenCardTemplateSearchModal';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { AutomationSelectorModal } from '../../../../components/automation/AutomationSelector';
import { AddContactConnection } from '../../../../components/contacts/AddContactConnection';
import { AssignContactOwner } from '../../../../components/contacts/AssignContactOwner';
import { ContactsAddTagModal } from '../../../../components/contacts/ContactsAddTagModal';
import { ContactsRemoveTagModal } from '../../../../components/contacts/ContactsRemoveTagModal';
import { MergeContactsWizard } from '../../../../components/contacts/MergeContactsWizard';
import { ArchiveIcon } from '../../../../components/svgs/icons/ArchiveIcon';
import { MoreIcon } from '../../../../components/svgs/icons/MoreIcon';
import { WarningIcon } from '../../../../components/svgs/icons/WarningIcon';
import { ROUTES } from '../../../../constants/routes';
import { usePeopleSearch } from '../PeopleContext';
import { useStatusFilters } from '../hooks/useStatusFilter';
import { ActionBarButton } from './ActionBarButton';
import { ButtonRenderer } from './ButtonRenderer';
import { styleSheet } from './styles';
import { useActionBarStatus, useSystemJob } from './useActionBarStatus';
import { useActionButtons } from './useActionButtons';
import { useButtonConfiguration } from './useButtonConfiguration';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	filter: Api.IBulkContactsRequest;
	sort: Api.ISortDescriptor<string>;
	onReload: ({ newFilter, newSort }: { newFilter?: Api.IBulkContactsRequest; newSort?: Api.ISortDescriptor }) => void;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}

const RestoreConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isCta: true,
		title: 'Restore',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const ResubscribeTextsConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isCta: true,
		title: 'Resubscribe',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const ConfirmVisibilityChange: IConfirmationDialogOption[] = [
	{
		isCta: true,
		representedObject: true,
		title: 'Confirm',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

export const PeopleActionsBar: React.FC<IProps> = observer(
	({ className, styles, filter, sort, onReload, onChangeFilter }) => {
		const userSession = useUserSession();
		const fullscreenModal = useFullscreenModal();
		const history = useHistory();
		const { pushApiError } = useErrorMessages();

		// Use the new context-based approach
		const {
			selectionState,
			selectedContactIds,
			setSelectedContactIds,
			setSelectionState,
			excludedContactIds,
			setExcludedContactIds,
			contacts,
		} = usePeopleSearch();

		const clearSelectionState = () => {
			setSelectedContactIds([]);
			onReload({});
		};

		// Get display name for a contact
		const getContactDisplayName = (contact: Api.IContact) => {
			return getDisplayName(contact);
		};

		const { isActive, isSelectingAll, canKeepInTouch, canMerge, canRestore, canFocus } = useActionBarStatus();

		const [isContactOwnerModalOpen, setIsContactOwnerModalOpen] = React.useState(false);
		const [isContactConnectionModalOpen, setIsContactConnectionModalOpen] = React.useState(false);
		const [isMergeModalOpen, setIsMergeModalOpen] = React.useState(false);

		const [isAutomationOpen, setIsAutomationOpen] = React.useState(false);
		const [isMoreMenuOpen, setIsMoreMenuOpen] = React.useState(false);
		const { isArchived } = useStatusFilters({ filter, onChangeFilter });

		const exportContactsMutation = useExportContactsMutation({
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});
		const exportContactsPolicyReportMutation = useExportContactsPolicyReportMutation({
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		// Set up contact mutations
		const contactSetKeepInTouchMutation = useContactSetKeepInTouchMutation({
			onSuccess: () => {
				setSelectedContactIds([]);
				invalidateAllContactFilterQueries();
				onReload({});
			},
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		const contactDeleteMutation = useContactDeleteMutation({
			onSuccess: (_result: Api.IBulkOperationResult<string>) => {
				toast.success(`${selectedContactIds.length} contacts deleted`);
				setSelectedContactIds([]);
				onReload({});
				invalidateAllContactFilterQueries();
			},
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		const contactDeleteManyMutation = useContactDeleteManyMutation({
			onSuccess: (result: number) => {
				toast.success(`${result} contacts deleted`);
				setSelectedContactIds([]);
				onReload({});
				invalidateAllContactFilterQueries();
			},
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		const bulkToggleTextSubscriptionMutation = useBulkContactToggleTextSubscriptionMutation({
			onSuccess: (job, variables) => {
				bulkSMSToggleDirectionRef.current = variables.optOut ? 'unsubscribe' : 'resubscribe';
				startWatchingBulkSMSToggleJob(job);
				// Clear all three pieces of selection state together — `selectionState === Some` with an empty
				// `selectedContactIds` is interpreted by PeopleTable as "select-all-with-exclusions", which would
				// flash every row as checked during the brief window before the job-complete refetch resets state.
				setSelectedContactIds([]);
				setExcludedContactIds([]);
				setSelectionState(Api.ESelectionState.None);
			},
			onError: error => {
				pushApiError(error);
			},
		});

		const buildBulkSMSToggleRequest = (optOut: boolean): Api.IContactsAutomatedSmsOptOutRequest => ({
			excludeContactIds: isSelectingAll ? excludedContactIds : undefined,
			filter: isSelectingAll ? filter.filter : undefined,
			ownershipFilter: isSelectingAll ? filter.ownershipFilter : undefined,
			includeContactIds: isSelectingAll ? undefined : selectedContactIds,
			optOut,
		});

		// Set up visibility and unarchive mutations
		const contactSetVisibilityMutation = useContactSetVisibilityMutation({
			onSuccess: data => {
				startWatchingJob(data);
			},
			onError: error => {
				pushApiError(error);
			},
		});

		const contactUnarchiveMutation = useContactUnarchiveMutation({
			onSuccess: restoredCount => {
				toast.success(
					restoredCount > 1
						? `${restoredCount} contacts were restored`
						: selectedContactIds.length && selectedContacts.length > 0
							? getContactDisplayName(selectedContacts[0]) + ' was restored'
							: `${restoredCount} contact restored`
				);

				setSelectedContactIds([]);
				onReload({});
			},
			onError: error => {
				pushApiError(error);
			},
		});

		const onAutomationsSuccess = () => {
			clearSelectionState();
			setIsAutomationOpen(false);
		};

		// Set up visibility job monitoring
		const { startWatchingJob, isJobRunning: _isJobRunning } = useSystemJob(() => {
			// Job completed callback
			onReload({});
			invalidateAllContactFilterQueries();
		});

		// Track which direction the in-flight bulk-SMS job represents so we can word the completion toast.
		const bulkSMSToggleDirectionRef = React.useRef<'unsubscribe' | 'resubscribe' | null>(null);
		const { startWatchingJob: startWatchingBulkSMSToggleJob } = useSystemJob((job: Api.ISystemJob) => {
			const direction = bulkSMSToggleDirectionRef.current;
			const succeeded = job?.recordsSucceeded ?? 0;
			const failed = job?.recordsFailed ?? 0;
			const verb = direction === 'resubscribe' ? 'resubscribed to' : 'unsubscribed from';
			const actionWord = direction === 'resubscribe' ? 'resubscribe' : 'unsubscribe';
			if (succeeded === 0 && failed > 0) {
				toast.error(`Failed to ${actionWord} contacts from automated texts (${failed} failed)`);
			} else {
				toast.success(
					`${succeeded} contact${succeeded === 1 ? '' : 's'} ${verb} automated texts` +
						(failed > 0 ? ` (${failed} failed)` : '')
				);
			}
			bulkSMSToggleDirectionRef.current = null;
			onReload({});
			invalidateAllContactFilterQueries();
		});

		const canShowAutomations = React.useRef(
			userSession?.account?.features?.automation?.enabled && userSession?.featurePermissions.automation.read
		).current;
		const canShowCards = React.useRef(userSession?.account?.features?.handwrittenCards?.enabled).current;
		const [visibilityChangeType, setVisibilityChangeType] = React.useState<'admin' | 'all'>(null);
		const [isHwcTemplateChooserModalOpen, setIsHwcTemplateChooserModalOpen] = React.useState(false);
		const [routeAfterHwcTemplateSelection, setRouteAfterHwcTemplateSelection] = React.useState<'contacts' | 'filter'>(
			null
		);

		const canSendTextingCampaign =
			userSession?.account?.features?.texting?.enabled &&
			userSession.account.features.automation?.additionalAllowedTriggers?.includes(Api.AutomationTriggerType.Texting);
		const canManageSmsOptOut = !!userSession?.account?.features?.texting?.enabled;
		const duplicateContactsFeatureFlag = !!userSession.account.preferences.showDuplicateContacts;

		// Get selected contacts for UI display
		const selectedContacts = React.useMemo(() => {
			return contacts.filter(contact => selectedContactIds.includes(contact.id));
		}, [contacts, selectedContactIds]);

		// Keep in touch functionality
		const onKeepInTouch = (frequency: number) => {
			if (frequency > -1) {
				const keepInTouches = selectedContacts.map(x => ({
					contactId: x.id,
					accountId: userSession.account.id,
					userId: userSession.user.id,
					frequency,
				}));

				contactSetKeepInTouchMutation.mutate(keepInTouches);
			}
		};

		const onSendMessage = () => {
			if (selectionState === Api.ESelectionState.All || selectionState === Api.ESelectionState.Some) {
				if (selectedContactIds.length === 0) {
					const allWasSelected = filter.includeContactIds?.length === 0;
					if (!allWasSelected) {
						const flatContacts = contacts;

						const contactsToSend: Api.ContactViewModel[] = [];
						flatContacts
							.filter(c => !excludedContactIds.includes(c.id))
							.forEach(x => {
								contactsToSend.push(new Api.ContactViewModel(userSession, x));
								if (x.duplicateContactIds?.length) {
									x.duplicateContactIds.forEach(id => {
										const duplicateContact = contacts.find(c => c.id === id);
										if (duplicateContact) {
											contactsToSend.push(new Api.ContactViewModel(userSession, duplicateContact));
										}
									});
								}
							});

						fullscreenModal.history?.push({
							pathname: '/email/campaigns/create/from-selection',
							state: {
								model: {
									context: {
										allowSenderSelection: true,
										bulkFilterRequestOptions: {
											readOnlySearchCriteria: true,
										},
										contacts: contactsToSend,
									},
									type: 'Contacts',
								},
							},
						});
						return;
					}

					// select all, but check exclusions
					const bulkFilterRequest: Api.IEmailMessageContactsFilterRequest = {
						contactFilterRequest: { criteria: filter.filter.criteria },
						groupByDuplicate: duplicateContactsFeatureFlag,
						ownershipFilter: filter.ownershipFilter,
					};

					// apply sort
					if (sort && sort.sortBy) {
						bulkFilterRequest.sortAscending = sort.sort === 'asc';
						bulkFilterRequest.sortProperty = sort.sortBy as keyof Api.IContact;
					}

					const locationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
						model: {
							context: {
								allowSenderSelection: true,
								bulkFilterRequest,
								contactsToOmit: excludedContactIds?.length
									? excludedContactIds
											.map(id => {
												const contact = contacts.find(x => x.id === id);
												return contact ? new Api.ContactViewModel(userSession, contact) : null;
											})
											.filter(Boolean) // Filter out null/undefined values
									: [],
								contacts: filter.includeContactIds?.length
									? (contacts.filter(x => filter.includeContactIds.includes(x.id)) as any)
									: undefined,
							},
							type: 'Contacts',
						},
					};
					fullscreenModal.history?.push({
						pathname: '/email/campaigns/create/from-filter',
						state: locationState,
					});
				} else {
					// normal selection
					const locationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
						model: {
							context: {
								allowSenderSelection: true,
								bulkFilterRequestOptions: {
									readOnlySearchCriteria: true,
								},
								contacts: selectedContacts.map(x => new Api.ContactViewModel(userSession, x)),
							},
							type: 'Contacts',
						},
					};
					fullscreenModal.history?.push({
						pathname: '/email/campaigns/create/from-selection',
						state: locationState,
					});
				}
			}
		};

		const onSendCard = () => {
			if (selectionState === Api.ESelectionState.All || selectionState === Api.ESelectionState.Some) {
				if (selectedContactIds.length === 0) {
					setRouteAfterHwcTemplateSelection('filter');
					setIsHwcTemplateChooserModalOpen(true);
				} else {
					setRouteAfterHwcTemplateSelection('contacts');
					setIsHwcTemplateChooserModalOpen(true);
				}
			}
		};

		const onFocusContacts = () => {
			if (selectionState === Api.ESelectionState.All || selectionState === Api.ESelectionState.Some) {
				const searchParams = new URLSearchParams();
				searchParams.set('mode', 'contact');
				if (selectedContactIds?.length) {
					searchParams.set('initialContactIds', selectedContactIds.join(','));
				}
				if (excludedContactIds?.length) {
					searchParams.set('initialContactIdsToOmit', excludedContactIds.join(','));
				}
				if (filter?.filter?.criteria) {
					searchParams.set('initialFilterCriteriaList', JSON.stringify(filter.filter.criteria));
				}
				if (filter?.ownershipFilter) {
					searchParams.set('ownershipFilter', JSON.stringify(filter.ownershipFilter));
				}
				history.push({
					pathname: `${ROUTES.contacts.people}/focused`,
					search: searchParams.toString(),
				});
			}
		};

		const onSendTextClicked = () => {
			if (selectedContactIds.length > 0) {
				fullscreenModal.history.push({
					pathname: `/texting-campaign/contacts`,
					state: {
						contactIds: selectedContactIds,
					},
				});
			} else {
				fullscreenModal.history.push({
					pathname: `/texting-campaign/filter`,
					state: {
						initialContactIdsToOmit: excludedContactIds,
						initialFilterCriteriaList: filter.filter.criteria,
						initialOwnershipFilter: filter.ownershipFilter,
					},
				});
			}
		};

		const onHwcTemplateClicked = (template: Api.IHandwrittenCardTemplate) => {
			if (routeAfterHwcTemplateSelection === 'contacts') {
				fullscreenModal.history.push({
					pathname: `/bulk-contact-selection-postcard/${template.id}`,
					state: {
						contactIds: selectedContactIds,
					},
				});
			} else if (routeAfterHwcTemplateSelection === 'filter') {
				fullscreenModal.history.push({
					pathname: `/bulk-contact-filter-postcard/${template.id}`,
					state: {
						contactIdsToOmit: excludedContactIds,
						tagFilterCriterias: filter.filter.criteria,
						ownershipFilter: filter.ownershipFilter,
					},
				});
			}
			setIsHwcTemplateChooserModalOpen(false);
			setRouteAfterHwcTemplateSelection(null);
		};

		// Modal handlers
		const restoreModal = useModal(
			false,
			(result: IConfirmationDialogOption) => {
				if (result.isCta) {
					contactUnarchiveMutation.mutate({
						excludeContactIds: excludedContactIds,
						filter: isSelectingAll ? filter.filter : null,
						ownershipFilter: isSelectingAll ? filter.ownershipFilter : null,
						includeContactIds: selectedContactIds,
					});
				}
			},
			[contactUnarchiveMutation, selectedContactIds, excludedContactIds, isSelectingAll, filter]
		);

		const keepInTouchModal = useModal(
			false,
			(frequency: number) => {
				onKeepInTouch(frequency);
			},
			[onKeepInTouch]
		);

		const addTagModal = useModal<string>(
			false,
			_selectedTag => {
				invalidateAllContactFilterQueries();
			},
			[invalidateAllContactFilterQueries]
		);

		const removeTagModal = useModal<string>(
			false,
			_selectedTag => {
				invalidateAllContactFilterQueries();
				toast.success(`Tag removed successfully`);
			},
			[invalidateAllContactFilterQueries]
		);

		const openMergeModal = () => {
			setIsMergeModalOpen(true);
		};

		const closeMergeModal = () => {
			clearSelectionState();
			setIsMergeModalOpen(false);
		};

		const openContactOwnerModal = () => {
			setIsContactOwnerModalOpen(true);
			setIsMoreMenuOpen(false);
		};

		const openContactConnectionModal = () => {
			setIsContactConnectionModalOpen(true);
			setIsMoreMenuOpen(false);
		};

		const onExportContactsButtonClicked = () => {
			if (exportContactsMutation.isIdle) {
				setIsMoreMenuOpen(false);

				// filter and includeContactIds are mutually exclusive:
				// - When using filter mode (Select All): send filter + excludeContactIds
				// - When selecting specific contacts: send includeContactIds only
				const exportRequest = {
					excludeContactIds: isSelectingAll ? excludedContactIds : undefined,
					filter: isSelectingAll ? filter.filter : undefined,
					ownershipFilter: isSelectingAll ? filter.ownershipFilter : undefined,
					includeContactIds: isSelectingAll ? undefined : selectedContactIds,
				};

				exportContactsMutation.mutate({ exportRequest });
			}
		};

		const onExportContactsClose = () => {
			exportContactsMutation.reset();
		};

		const onExportContactsPolicyReportButtonClicked = () => {
			if (exportContactsPolicyReportMutation.isIdle) {
				setIsMoreMenuOpen(false);

				// filter and includeContactIds are mutually exclusive:
				// - When using filter mode (Select All): send filter + excludeContactIds
				// - When selecting specific contacts: send includeContactIds only
				const exportRequest = {
					excludeContactIds: isSelectingAll ? excludedContactIds : undefined,
					filter: isSelectingAll ? filter.filter : undefined,
					ownershipFilter: isSelectingAll ? filter.ownershipFilter : undefined,
					includeContactIds: isSelectingAll ? undefined : selectedContactIds,
				};

				exportContactsPolicyReportMutation.mutate({ exportRequest });
			}
		};

		const onExportContactsPolicyReportModalClose = () => {
			exportContactsPolicyReportMutation.reset();
		};

		const deleteModal = useModal(
			false,
			(shouldDelete: boolean) => {
				if (!shouldDelete) {
					return;
				}

				if (isSelectingAll) {
					contactDeleteManyMutation.mutate({
						excludeContactIds: excludedContactIds,
						filter: filter.filter,
						ownershipFilter: filter.ownershipFilter,
						includeContactIds: [],
					});
				} else {
					contactDeleteMutation.mutate({ ids: selectedContactIds });
				}
			},
			[isSelectingAll, contactDeleteManyMutation, contactDeleteMutation, excludedContactIds, filter, selectedContactIds]
		);

		const onDeleteClicked = () => {
			deleteModal.setIsOpen(true)();
			setIsMoreMenuOpen(false);
		};

		const bulkUnsubscribeTextsModal = useModal(
			false,
			(shouldUnsubscribe: boolean) => {
				if (!shouldUnsubscribe) {
					return;
				}
				bulkToggleTextSubscriptionMutation.mutate(buildBulkSMSToggleRequest(true));
			},
			[bulkToggleTextSubscriptionMutation, isSelectingAll, excludedContactIds, filter, selectedContactIds]
		);

		const bulkResubscribeTextsModal = useModal(
			false,
			(result: IConfirmationDialogOption) => {
				if (!result || result.isCancel) {
					return;
				}
				bulkToggleTextSubscriptionMutation.mutate(buildBulkSMSToggleRequest(false));
			},
			[bulkToggleTextSubscriptionMutation, isSelectingAll, excludedContactIds, filter, selectedContactIds]
		);

		const onBulkUnsubscribeTextsClicked = () => {
			bulkUnsubscribeTextsModal.setIsOpen(true)();
			setIsMoreMenuOpen(false);
		};

		const onBulkResubscribeTextsClicked = () => {
			bulkResubscribeTextsModal.setIsOpen(true)();
			setIsMoreMenuOpen(false);
		};

		const confirmationDialogModal = useModal(
			false,
			(result: IConfirmationDialogOption) => {
				if (result) {
					onVisibilityChanged(visibilityChangeType);
				}
				setVisibilityChangeType(null);
			},
			[visibilityChangeType]
		);

		const openConfirmationDialogModal = (visibility: 'admin' | 'all') => () => {
			setVisibilityChangeType(visibility);
			confirmationDialogModal.setIsOpen(true)();
			setIsMoreMenuOpen(false);
		};

		const onVisibilityChanged = (visibility: 'admin' | 'all') => {
			const request = {
				excludeContactIds: excludedContactIds,
				filter: isSelectingAll ? filter.filter : null,
				ownershipFilter: isSelectingAll ? filter.ownershipFilter : null,
				includeContactIds: selectedContactIds,
				visibility,
			};

			contactSetVisibilityMutation.mutate(request);
		};

		// Reference to measure container width
		const containerRef = React.useRef<HTMLDivElement>(null);

		// Track container width
		const [containerWidth, setContainerWidth] = React.useState(0);
		// Track whether the keep in touch dropdown is open
		const [isKeepInTouchOpen, setIsKeepInTouchOpen] = React.useState(false);

		// Use ResizeObserver to detect container width changes
		React.useEffect(() => {
			if (!containerRef.current) return;

			const resizeObserver = new ResizeObserver(entries =>
				debounce(() => {
					if (entries[0]) {
						setContainerWidth(entries[0].contentRect.width);
					}
				}, 1)()
			);

			resizeObserver.observe(containerRef.current);
			return () => {
				resizeObserver.disconnect();
			};
		}, []);

		// Get action buttons
		const allButtons = useActionButtons({
			isActive,
			canKeepInTouch: !!canKeepInTouch,
			canMerge,
			canFocus,
			canRestore,
			isArchived,
			canShowAutomations,
			canShowCards,
			canSendTextingCampaign,
			canManageSmsOptOut,
			onKeepInTouch,
			onSendMessage,
			onSendTextClicked,
			onSendCard,
			onFocusContacts,
			openConfirmationDialogModal,
			openContactOwnerModal,
			openContactConnectionModal,
			openMergeModal,
			onDeleteClicked,
			onBulkUnsubscribeTextsClicked,
			onBulkResubscribeTextsClicked,
			addTagModal,
			removeTagModal,
			restoreModal,
			keepInTouchModal,
			onExportContactsButtonClicked,
			onExportContactsPolicyReportButtonClicked,
			setIsAutomationOpen,
			selectionState,
			filter,
		});

		// Determine which buttons to show and which to put in "More" menu
		const { visibleButtons: buttonVisibleButtons, moreMenuButtons: buttonMoreMenuButtons } = useButtonConfiguration(
			allButtons,
			containerWidth
		);

		// Find the keep in touch button to use in the popover
		const keepInTouchButton = React.useMemo(() => {
			return allButtons.find(btn => btn.key === 'keepInTouch');
		}, [allButtons]);

		// Extract all filtered tag names
		const filteredTagNames = React.useMemo(() => {
			if (!filter?.filter?.criteria) {
				return [];
			}

			// Check for tag criteria at the top level
			let tagCriteria = filter.filter.criteria.filter(c => c.property === Api.ContactFilterCriteriaProperty.Tag);

			// If not found at top level, check nested criteria (common for tags with OR operator)
			if (tagCriteria.length === 0) {
				filter.filter.criteria.forEach(criterion => {
					if (criterion.criteria && Array.isArray(criterion.criteria)) {
						const nestedTagCriteria = criterion.criteria.filter(
							c => c.property === Api.ContactFilterCriteriaProperty.Tag
						);
						tagCriteria = tagCriteria.concat(nestedTagCriteria);
					}
				});
			}

			return tagCriteria.map(c => c.value).filter(Boolean);
		}, [filter]);

		return (
			<div ref={containerRef} className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				{userSession.userRole === 'superAdmin' && isArchived() ? (
					<>
						<ActionBarButton
							icon={<ArchiveIcon />}
							onClick={restoreModal.setIsOpen(true)}
							disabled={!canRestore}
							styleClass={styleSheet.keepInTouch}
							actionButtonType='standard'
						>
							Restore selected contacts
						</ActionBarButton>
						<ConfirmationDialog
							modalProps={{
								isOpen: restoreModal.isOpen,
								onRequestClose: restoreModal.onRequestClose,
							}}
							options={RestoreConfirmationOptions}
							title='Restore selected contacts'
						>
							<div className={css(styleSheet.restoreContactText)}>Would you like to restore the selected contacts?</div>
						</ConfirmationDialog>
					</>
				) : (
					<>
						<div className={css(styleSheet.actionButtonsContainer)}>
							{/* Other visible buttons */}
							<ButtonRenderer
								allButtons={allButtons.filter(btn => btn.key !== 'keepInTouch')}
								visibleButtons={buttonVisibleButtons.filter(key => key !== 'keepInTouch')}
							/>
							{/* Keep in Touch Button with Popover */}
							{keepInTouchButton && buttonVisibleButtons.includes('keepInTouch') && (
								<Popover open={isKeepInTouchOpen} onOpenChange={setIsKeepInTouchOpen}>
									<PopoverTrigger asChild>
										<button
											onClick={() => setIsKeepInTouchOpen(!isKeepInTouchOpen)}
											className={css(
												styleSheet.actionButton,
												keepInTouchButton.disabled ? styleSheet.actionButtonDisabled : styleSheet.actionButtonEnabled,
												keepInTouchButton.styleClass
											)}
											disabled={keepInTouchButton.disabled}
										>
											{keepInTouchButton.icon}
											<span className={css(styleSheet.actionButtonText)}>{keepInTouchButton.children}</span>
										</button>
									</PopoverTrigger>
									<PopoverContent>
										<div className={css(styleSheet.keepInTouchMenu)}>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(1);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every month</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(2);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every 2 months</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(3);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every 3 months</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(6);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every 6 months</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(12);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every year</span>
											</button>
										</div>
									</PopoverContent>
								</Popover>
							)}

							{buttonMoreMenuButtons.length > 0 && (
								<Popover open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
									<PopoverTrigger asChild>
										<button
											onClick={() => {
												setIsMoreMenuOpen(!isMoreMenuOpen);
											}}
										>
											<MoreIcon />
										</button>
									</PopoverTrigger>
									<PopoverContent>
										<div className={css(styleSheet.moreMenuContainer)}>
											{buttonMoreMenuButtons.map(btn => {
												return (
													<div key={btn.key} className={css(styleSheet.moreMenuButton)}>
														<ActionBarButton
															key={btn.key}
															icon={btn.icon}
															onClick={btn.onClick}
															disabled={btn.disabled}
															styleClass={btn.styleClass}
															isFullWidth
															actionButtonType={btn.actionButtonType}
														>
															{btn.children}
														</ActionBarButton>
													</div>
												);
											})}
										</div>
									</PopoverContent>
								</Popover>
							)}
						</div>
						<ContactsAddTagModal
							request={{
								excludeContactIds: excludedContactIds,
								includeContactIds: selectedContactIds,
								filter: isSelectingAll ? filter.filter : undefined,
								ownershipFilter: isSelectingAll ? filter.ownershipFilter : undefined,
							}}
							modalProps={{
								isOpen: addTagModal.isOpen,
								onRequestClose: addTagModal.onRequestClose,
							}}
						/>
						{filteredTagNames.length > 0 && (
							<ContactsRemoveTagModal
								tagNames={filteredTagNames}
								request={{
									excludeContactIds: excludedContactIds,
									includeContactIds: selectedContactIds,
									filter: isSelectingAll ? filter.filter : undefined,
									ownershipFilter: isSelectingAll ? filter.ownershipFilter : undefined,
								}}
								totalContactCount={isSelectingAll ? contacts.length - excludedContactIds.length : undefined}
								modalProps={{
									isOpen: removeTagModal.isOpen,
									onRequestClose: removeTagModal.onRequestClose,
								}}
							/>
						)}
						<AddContactConnection
							selectedContactIds={selectedContactIds}
							excludedContactIds={excludedContactIds}
							filterCriteria={filter.filter}
							ownershipFilter={filter.ownershipFilter}
							includeFilterInRequest={isSelectingAll}
							isOpen={isContactConnectionModalOpen}
							onSave={() => setIsContactConnectionModalOpen(false)}
							onCancel={() => setIsContactConnectionModalOpen(false)}
						/>
						<AssignContactOwner
							selectedContactIds={selectedContactIds}
							excludedContactIds={excludedContactIds}
							filterCriteria={filter.filter}
							ownershipFilter={filter.ownershipFilter}
							includeFilterInRequest={isSelectingAll}
							isOpen={isContactOwnerModalOpen}
							onSuccess={() => setIsContactOwnerModalOpen(false)}
							onCancel={() => setIsContactOwnerModalOpen(false)}
						/>
						{isAutomationOpen ? (
							<AutomationSelectorModal
								contactSelection={{
									includeContactIds: selectedContactIds,
									excludeContactIds: excludedContactIds,
								}}
								filterCriteria={isSelectingAll ? filter.filter : null}
								ownershipFilter={isSelectingAll ? filter.ownershipFilter : null}
								onDeselectAll={() => setSelectedContactIds([])}
								closeModal={onAutomationsSuccess}
								startAutomationForSelectedContacts={true}
							/>
						) : null}
						<ConfirmationDialog
							className={css(styleSheet.visibilityModalContainer)}
							icon={<WarningIcon />}
							modalProps={{
								isOpen: confirmationDialogModal.isOpen,
								onRequestClose: confirmationDialogModal.onRequestClose,
							}}
							options={ConfirmVisibilityChange}
						>
							<div>
								<div className={css(styleSheet.visibilityModalTitle)}>Change visibility</div>
								<div className={css(styleSheet.visibilityModalTextContainer)}>
									Are you sure you want to change the visibility of selected contacts so
									<span className={css(styleSheet.visibilityModalTextBold)}>
										{visibilityChangeType === 'admin'
											? ' only you can see them'
											: ' all Levitate users on your account can see them'}
										?
									</span>
								</div>
							</div>
						</ConfirmationDialog>
						{isMergeModalOpen && <MergeContactsWizard selectedContacts={selectedContacts} onFinish={closeMergeModal} />}
						<ExportConfirmationModalV2
							mutationResult={exportContactsMutation}
							modalProps={{
								isOpen: !exportContactsMutation.isIdle,
								onRequestClose: onExportContactsClose,
							}}
							subTitle={
								<>
									<p className={css(styleSheet.noMargin)}>
										The fields we&apos;ll export include: Name, Company, Job Title,
									</p>
									<p className={css(styleSheet.noMargin)}>Address, Phone Numbers, Email Address, OwnerID, and tags.</p>
								</>
							}
						/>
						<ExportConfirmationModalV2
							mutationResult={exportContactsPolicyReportMutation}
							modalProps={{
								isOpen: !exportContactsPolicyReportMutation.isIdle,
								onRequestClose: onExportContactsPolicyReportModalClose,
							}}
							subTitle={
								<>
									<p className={css(styleSheet.noMargin)}>
										The fields we&apos;ll export include: Name, First Name, Email,
									</p>
									<p className={css(styleSheet.noMargin)}>Owner, Line of Business, Carrier, and Renewal Date.</p>
								</>
							}
						/>
						<DeleteConfirmation
							bodyText='Are you sure you want to delete these contacts?'
							isOpen={deleteModal.isOpen}
							onFinish={deleteModal.onRequestClose}
						/>
						<DeleteConfirmation
							title='Unsubscribe from automated texts'
							bodyText={
								isSelectingAll
									? 'The selected contacts will no longer receive automated text messages until manually re-subscribed.'
									: `${selectedContactIds.length} contact${
											selectedContactIds.length === 1 ? '' : 's'
										} will no longer receive automated text messages until manually re-subscribed.`
							}
							destructiveButtonText='Unsubscribe'
							isOpen={bulkUnsubscribeTextsModal.isOpen}
							onFinish={bulkUnsubscribeTextsModal.onRequestClose}
							showSpinner={bulkToggleTextSubscriptionMutation.isPending}
						/>
						<ConfirmationDialog
							modalProps={{
								isOpen: bulkResubscribeTextsModal.isOpen,
								onRequestClose: bulkResubscribeTextsModal.onRequestClose,
							}}
							options={ResubscribeTextsConfirmationOptions}
							title='Resubscribe to automated texts'
						>
							<div className={css(styleSheet.restoreContactText)}>
								{isSelectingAll
									? 'The selected contacts will be eligible to receive automated text messages again.'
									: `${selectedContactIds.length} contact${
											selectedContactIds.length === 1 ? '' : 's'
										} will be eligible to receive automated text messages again.`}
							</div>
						</ConfirmationDialog>
						{isHwcTemplateChooserModalOpen ? (
							<HandwrittenCardTemplateSearchModal
								isOpen={true}
								onClose={() => setIsHwcTemplateChooserModalOpen(false)}
								onTemplateClicked={onHwcTemplateClicked}
							/>
						) : null}
					</>
				)}
			</div>
		);
	}
);
